import { Fragment, useEffect, useRef, useState } from "react";
import { apiClient, API } from "@/context/AuthContext";
import { Bot, Loader2, MessageSquarePlus, Send, Sparkles, User } from "lucide-react";

const MODELS = [
  { id: "claude", label: "Claude", sub: "Sonnet 5.5" },
  { id: "gemini", label: "Gemini", sub: "3.1 Pro" },
  { id: "chatgpt", label: "ChatGPT", sub: "GPT-5.5" },
];
const SUGGESTIONS = [
  "Bu binada kaç yangın söndürücü gerekiyor?",
  "Uygunsuz maddeleri öncelik sırasıyla özetle",
  "Kaçış yolu genişliği için ne yapmalıyız?",
  "Bina sahibine sade dille ne söylemeliyim?",
];

const inline = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((p, i) => p.startsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : <Fragment key={i}>{p}</Fragment>);
const Markdown = ({ text }) => text.split("\n").map((line, i) => {
  const t = line.trim();
  if (!t) return <br key={i}/>;
  if (/^#{1,4}\s/.test(t)) return <h4 key={i}>{inline(t.replace(/^#+\s/, ""))}</h4>;
  if (/^[-*•]\s/.test(t)) return <li key={i}>{inline(t.slice(2))}</li>;
  if (/^\d+[.)]\s/.test(t)) return <li key={i} className="num">{inline(t)}</li>;
  return <p key={i}>{inline(t)}</p>;
});

async function streamChat(assessmentId, body, onEvent) {
  const res = await fetch(`${API}/assessments/${assessmentId}/assistant/stream`, {
    method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Asistan cevap veremedi");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const chunks = buf.split("\n\n");
    buf = chunks.pop();
    chunks.forEach((c) => { if (c.startsWith("data: ")) onEvent(JSON.parse(c.slice(6))); });
  }
}

export default function AssistantPanel({ assessmentId }) {
  const [provider, setProvider] = useState("claude");
  const [convs, setConvs] = useState([]);
  const [convId, setConvId] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef();

  const loadConvs = () => apiClient.get(`/assessments/${assessmentId}/assistant/conversations`).then((r) => setConvs(r.data)).catch(() => {});
  useEffect(() => { loadConvs(); }, [assessmentId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [msgs]);

  const openConv = async (id) => {
    setConvId(id); setError("");
    const { data } = await apiClient.get(`/assistant/conversations/${id}/messages`);
    setMsgs(data.messages);
    return data;
  };

  const waitForReply = async (id) => {
    for (let i = 0; i < 30; i++) {
      await new Promise((r) => setTimeout(r, 4000));
      const data = await openConv(id);
      if (!data.pending) return;
    }
  };

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput(""); setBusy(true); setError("");
    const model = MODELS.find((m) => m.id === provider);
    setMsgs((m) => [...m, { role: "user", content: message, id: `u${Date.now()}` }, { role: "assistant", content: "", model: `${model.label} ${model.sub}`, id: "live", live: true }]);
    let cid = convId, gotDone = false;
    try {
      await streamChat(assessmentId, { message, provider, conversation_id: convId }, (ev) => {
        if (ev.type === "start") { cid = ev.conversation_id; setConvId(ev.conversation_id); }
        if (ev.type === "delta") setMsgs((m) => m.map((x) => x.id === "live" ? { ...x, content: x.content + ev.content } : x));
        if (ev.type === "error") setError(ev.detail);
        if (ev.type === "done") gotDone = true;
      });
      if (!gotDone && cid) await waitForReply(cid);
      else setMsgs((m) => m.map((x) => x.id === "live" ? { ...x, live: false, id: `a${Date.now()}` } : x));
    } catch (err) {
      if (cid) await waitForReply(cid); else setError(err.message);
    } finally { setBusy(false); loadConvs(); }
  };

  const newChat = () => { setConvId(null); setMsgs([]); setError(""); };

  return (
    <section className="section-block assistant" data-testid="assistant-panel">
      <aside className="assistant-history" data-testid="assistant-history">
        <button className="primary-btn" onClick={newChat} data-testid="assistant-new-chat"><MessageSquarePlus size={14}/> Yeni sohbet</button>
        {convs.map((c) => (
          <button key={c.id} className={`assistant-conv${c.id === convId ? " active" : ""}`} onClick={() => openConv(c.id)} data-testid={`assistant-conv-${c.id}`}>
            <strong>{c.title}</strong><small>{new Date(c.updated_at).toLocaleString("tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} · {c.created_by}</small>
          </button>
        ))}
        {!convs.length && <small className="evidence-empty">Kayıtlı sohbet yok.</small>}
      </aside>
      <div className="assistant-main">
        <div className="assistant-head">
          <div><div className="eyebrow"><Sparkles size={11}/> YÖNETMELİK ASİSTANI</div><h2>Bu bina hakkında sorun</h2></div>
          <div className="model-switch" data-testid="assistant-model-switch">
            {MODELS.map((m) => (
              <button key={m.id} className={provider === m.id ? "active" : ""} onClick={() => setProvider(m.id)} disabled={busy} data-testid={`assistant-model-${m.id}`}>
                <strong>{m.label}</strong><small>{m.sub}</small>
              </button>
            ))}
          </div>
        </div>
        <div className="assistant-thread" data-testid="assistant-thread">
          {!msgs.length && (
            <div className="assistant-empty">
              <Bot size={28}/>
              <p>Asistan bu binanın bilgilerini ve bulgularını biliyor. Bir soru yazın ya da aşağıdakilerden birini seçin.</p>
              <div className="assistant-suggest">{SUGGESTIONS.map((s, i) => <button key={s} onClick={() => send(s)} data-testid={`assistant-suggestion-${i}`}>{s}</button>)}</div>
            </div>
          )}
          {msgs.map((m) => (
            <div key={m.id} className={`bubble ${m.role}`} data-testid={`assistant-msg-${m.role}`}>
              <span className="bubble-icon">{m.role === "user" ? <User size={14}/> : <Bot size={14}/>}</span>
              <div className="bubble-body">
                {m.role === "assistant" && <small className="bubble-model">{m.model}</small>}
                {m.content ? <Markdown text={m.content}/> : <span className="thinking"><Loader2 size={14} className="spin"/> Düşünüyor… (Gemini 30–60 sn sürebilir)</span>}
              </div>
            </div>
          ))}
          <div ref={endRef}/>
        </div>
        {error && <div className="auth-error" data-testid="assistant-error">{error}</div>}
        <form className="assistant-input" onSubmit={(e) => { e.preventDefault(); send(); }}>
          <textarea rows={2} value={input} onChange={(e) => setInput(e.target.value)} placeholder="Sorunuzu yazın… (Enter ile gönder, Shift+Enter yeni satır)"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} disabled={busy} data-testid="assistant-input"/>
          <button className="primary-btn" disabled={busy || !input.trim()} data-testid="assistant-send">{busy ? <Loader2 size={15} className="spin"/> : <Send size={15}/>}</button>
        </form>
        <small className="legal-foot">Asistanın cevapları yardımcı niteliktedir; resmi uygunluk kararı yetkili itfaiye kurumunca verilir.</small>
      </div>
    </section>
  );
}
